import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from './organizations';
import { defaultSections, type FormField } from './intakeFormConfig';
import debug from '@/lib/debug';

export interface IntakeFormSection {
  section_key: string;
  section_name: string;
  sort_order: number;
  enabled: boolean;
  fields: FormField[];
}

/**
 * Form template ids — picks the visual layout used to render the
 * public intake form. Each template is a fully-realized aesthetic
 * (typography, layout, image treatment), inheriting brand color
 * and uploaded images from the rest of theme_config.
 */
export type FormTemplateId =
  | 'editorial'   // magazine-style hero + serif headlines
  | 'bold'        // Typeform-style step-by-step, full-bleed
  | 'minimal'     // single-page, lots of whitespace, monoline
  | 'playful'     // rounded shapes, accent dots, friendly
  | 'polaroid'    // photo-first, taped/film aesthetic
  | 'heritage';   // cream + navy + gold, serif, boxed inputs

export interface IntakeFormThemeConfig {
  /// Visual template the form renders with. Defaults to 'minimal'
  /// when unset so legacy forms still render cleanly.
  template?: FormTemplateId;

  /// Brand colors. bgColor + textColor are kept for legacy support
  /// but new templates primarily use accentColor (the studio's
  /// brand color, mirrored from organization.brand_color).
  bgColor?: string;
  textColor?: string;
  accentColor?: string;

  /// Hero/cover image displayed at the top of the form (Editorial,
  /// Bold, Playful all use this; Minimal optional; Polaroid stylized).
  heroImageUrl?: string;
  /// Full-page background image (Bold + Polaroid use this prominently).
  backgroundImageUrl?: string;
  /// Per-section divider images, keyed by section_key. Optional;
  /// only Editorial + Polaroid render these.
  sectionImages?: Record<string, string>;
  /// Optional studio logo override. Falls back to organization.logo_url.
  logoUrl?: string;
}

export interface IntakeFormSettings {
  autoCreateProject?: boolean;
  sendConfirmationEmail?: boolean;
  redirectUrl?: string;
}

export interface IntakeForm {
  id: string;
  org_id: string;
  category_id: string | null;
  name: string;
  slug: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  sections: IntakeFormSection[];
  theme_config: IntakeFormThemeConfig;
  settings: IntakeFormSettings;
  created_at: string;
  updated_at: string;
}

/**
 * STRICT-1 — the row → IntakeForm boundary, in one place.
 *
 * `intake_forms` has five nullable columns this interface promises are
 * not: sections, theme_config, settings, is_default, is_active. Three
 * of them were already being coalesced at each of the four call sites;
 * the two booleans were not, and `form.is_active` could arrive
 * undefined into code that reads it as a boolean. The DB defaults are
 * the right fallbacks (active unless told otherwise, default only when
 * marked), so they live here rather than being re-decided per caller.
 */
const toIntakeForm = (row: Record<string, unknown>): IntakeForm => ({
  ...(row as unknown as IntakeForm),
  sections: Array.isArray(row.sections) ? (row.sections as unknown as IntakeFormSection[]) : [],
  theme_config: (row.theme_config || {}) as IntakeFormThemeConfig,
  settings: (row.settings || {}) as IntakeFormSettings,
  is_default: (row.is_default as boolean | null) ?? false,
  is_active: (row.is_active as boolean | null) ?? true,
  created_at: (row.created_at as string | null) ?? '',
  updated_at: (row.updated_at as string | null) ?? '',
});

export type IntakeFormInsert = Omit<IntakeForm, 'id' | 'created_at' | 'updated_at'>;
export type IntakeFormUpdate = Partial<IntakeFormInsert>;

// Generate a URL-safe slug from a name
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .substring(0, 50);
};

// Fetch all intake forms for the current org
export const useIntakeForms = () => {
  const { data: org } = useOrganization();

  return useQuery({
    queryKey: ['intake-forms', org?.id],
    queryFn: async (): Promise<IntakeForm[]> => {
      if (!org?.id) return [];

      const { data, error } = await supabase
        .from('intake_forms')
        .select('*')
        .eq('org_id', org.id)
        .order('created_at', { ascending: true });

      if (error) {
        debug.error({ action: 'fetchIntakeForms', table: 'intake_forms' }, error);
        throw error;
      }

      return (data || []).map(toIntakeForm);
    },
    enabled: !!org?.id,
  });
};

// Fetch a single intake form by ID
export const useIntakeForm = (formId: string | undefined) => {
  return useQuery({
    queryKey: ['intake-form', formId],
    queryFn: async (): Promise<IntakeForm | null> => {
      if (!formId) return null;

      const { data, error } = await supabase
        .from('intake_forms')
        .select('*')
        .eq('id', formId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null; // Not found
        throw error;
      }

      return toIntakeForm(data);
    },
    enabled: !!formId,
  });
};

// Fetch intake form by org slug and form slug (for public embed)
export const useIntakeFormBySlug = (orgSlug: string | undefined, formSlug: string | undefined) => {
  return useQuery({
    queryKey: ['intake-form-public', orgSlug, formSlug],
    queryFn: async (): Promise<{ form: IntakeForm; organization: any } | null> => {
      if (!orgSlug) return null;

      // Resolve the org past Phase 1's RLS lockdown. Tries the
      // `public-org-lookup` edge function first (deploys via Lovable
      // alongside this code), then the SECURITY DEFINER RPC (lands
      // when the SQL migration is applied), then falls back to a
      // direct table read for completeness. First win short-circuits.
      //
      // `orgSlug` here can be either a real slug or an org UUID —
      // Studio Sites embed forms with the UUID in the slug position
      // (see ContactFormSection in PublicSite.tsx). We detect and
      // route via `by_id` for UUIDs.
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const isUuid = UUID_RE.test(orgSlug);

      let org: any = null;

      try {
        const { data: efRes, error: efError } = await supabase.functions.invoke(
          'public-org-lookup',
          {
            body: isUuid
              ? { mode: 'by_id', org_id: orgSlug }
              : { mode: 'by_slug', slug: orgSlug },
          },
        );
        if (!efError && efRes && (efRes as any).data) {
          org = (efRes as any).data;
        }
      } catch (_err) {
        /* edge fn unavailable — fall through */
      }

      if (!org && !isUuid) {
        try {
          const { data: rpcData } = await (supabase as any)
            .rpc('get_public_org_by_slug', { _slug: orgSlug })
            .maybeSingle();
          if (rpcData) org = rpcData;
        } catch (_err) {
          /* RPC not present yet — fall through */
        }
      }

      if (!org) {
        let directQuery = supabase.from('organizations').select('*');
        directQuery = isUuid ? directQuery.eq('id', orgSlug) : directQuery.eq('slug', orgSlug);
        const { data: directData, error: directError } = await directQuery.maybeSingle();
        if (directError || !directData) return null;
        org = directData;
      }

      // If no form slug, get the default form
      let formQuery = supabase
        .from('intake_forms')
        .select('*')
        .eq('org_id', org.id)
        .eq('is_active', true);

      if (formSlug) {
        formQuery = formQuery.eq('slug', formSlug);
      } else {
        formQuery = formQuery.eq('is_default', true);
      }

      const { data: formData, error: formError } = await formQuery.maybeSingle();

      // If no specific form found, try to get any active form
      if (!formData && !formSlug) {
        const { data: anyForm } = await supabase
          .from('intake_forms')
          .select('*')
          .eq('org_id', org.id)
          .eq('is_active', true)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        
        if (anyForm) {
          return {
            form: toIntakeForm(anyForm),
            organization: org,
          };
        }
        return null;
      }

      if (formError || !formData) return null;

      return {
        form: toIntakeForm(formData),
        organization: org,
      };
    },
    enabled: !!orgSlug,
  });
};

// Create a new intake form
export const useCreateIntakeForm = () => {
  const queryClient = useQueryClient();
  const { data: org } = useOrganization();

  return useMutation({
    mutationFn: async (form: Omit<IntakeFormInsert, 'org_id'> & { formLimit?: number; formCount?: number }) => {
      if (!org?.id) throw new Error('Your studio isn’t set up yet');
      
      // Check tier limits if provided
      if (form.formLimit !== undefined && form.formCount !== undefined) {
        if (form.formCount >= form.formLimit) {
          throw new Error(`You've reached your limit of ${form.formLimit} intake form${form.formLimit === 1 ? '' : 's'}. Upgrade to create more.`);
        }
      }

      // Generate slug if not provided
      const slug = form.slug || generateSlug(form.name);

      // Check if this should be the default form (first form = default)
      const { count } = await supabase
        .from('intake_forms')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', org.id);

      const isDefault = (count || 0) === 0 || form.is_default;

      // If setting as default, unset other defaults
      if (isDefault) {
        await supabase
          .from('intake_forms')
          .update({ is_default: false })
          .eq('org_id', org.id);
      }

      // Remove form access props before inserting
      const { formLimit, formCount, ...formData } = form;

      const { data, error } = await supabase
        .from('intake_forms')
        .insert([{
          name: formData.name,
          slug,
          description: formData.description || null,
          category_id: formData.category_id || null,
          org_id: org.id,
          is_default: isDefault,
          is_active: formData.is_active ?? true,
          sections: (formData.sections || defaultSections) as any,
          theme_config: (formData.theme_config || {}) as any,
          settings: (formData.settings || {}) as any,
        }])
        .select()
        .single();

      if (error) {
        debug.error({ action: 'createIntakeForm', table: 'intake_forms' }, error);
        throw new Error(debug.parseSupabaseError(error));
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake-forms', org?.id] });
    },
  });
};

// Update an existing intake form
export const useUpdateIntakeForm = () => {
  const queryClient = useQueryClient();
  const { data: org } = useOrganization();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: IntakeFormUpdate }) => {
      // If setting as default, unset other defaults first
      if (updates.is_default && org?.id) {
        await supabase
          .from('intake_forms')
          .update({ is_default: false })
          .eq('org_id', org.id)
          .neq('id', id);
      }

      const { data, error } = await supabase
        .from('intake_forms')
        .update({
          ...updates,
          sections: updates.sections as any,
          theme_config: updates.theme_config as any,
          settings: updates.settings as any,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        debug.error({ action: 'updateIntakeForm', table: 'intake_forms' }, error);
        throw error;
      }

      return data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['intake-forms', org?.id] });
      queryClient.invalidateQueries({ queryKey: ['intake-form', id] });
    },
  });
};

// Delete an intake form
export const useDeleteIntakeForm = () => {
  const queryClient = useQueryClient();
  const { data: org } = useOrganization();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('intake_forms')
        .delete()
        .eq('id', id);

      if (error) {
        debug.error({ action: 'deleteIntakeForm', table: 'intake_forms' }, error);
        throw error;
      }

      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake-forms', org?.id] });
    },
  });
};

// Duplicate an intake form
export const useDuplicateIntakeForm = () => {
  const queryClient = useQueryClient();
  const { data: org } = useOrganization();

  return useMutation({
    mutationFn: async ({ formId, formLimit, formCount }: { formId: string; formLimit?: number; formCount?: number }) => {
      if (!org?.id) throw new Error('Your studio isn’t set up yet');
      
      // Check tier limits if provided
      if (formLimit !== undefined && formCount !== undefined) {
        if (formCount >= formLimit) {
          throw new Error(`You've reached your limit of ${formLimit} intake form${formLimit === 1 ? '' : 's'}. Upgrade to create more.`);
        }
      }

      // Fetch the original form
      const { data: original, error: fetchError } = await supabase
        .from('intake_forms')
        .select('*')
        .eq('id', formId)
        .single();

      if (fetchError || !original) throw new Error('Form not found');

      // Create duplicate with new name/slug
      const newName = `${original.name} (Copy)`;
      const newSlug = generateSlug(newName) + `-${Date.now()}`;

      const { data, error } = await supabase
        .from('intake_forms')
        .insert([{
          org_id: org.id,
          category_id: original.category_id,
          name: newName,
          slug: newSlug,
          description: original.description,
          is_default: false,
          is_active: false, // Start inactive
          sections: original.sections,
          theme_config: original.theme_config,
          settings: original.settings,
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intake-forms', org?.id] });
    },
  });
};
